import _ from "lodash";

// The CloudFront cookies that open private files (/auth signs them for this site's asset hosts) are written on
// .ambifi.com, so every AmbiFi site open in a browser shares one set: app.ambifi.com, app-eu/hca-eu and app-eu2/hca-eu2
// overwrite each other's when they sign in or refresh (every 15 minutes), and a site's private files then fail with the
// other site's cookies until its own next refresh. The guard keeps this site's cookies in the browser while the tab is
// in use: it puts them back when the tab is shown or focused, checks every two seconds while it shows, puts newer ones
// in after a refresh, and loads a private image again when it failed because another site's cookies were there. When a
// private image fails with this site's own cookies in place, they may have lapsed (they last two hours, and a tab that
// slept or sat hidden can show a page before its next refresh): the guard renews them and loads the image again. A
// hidden tab leaves the cookies alone, and of two AmbiFi sites showing at once, the one used last keeps them (the owner
// cookie names it).

export const CLOUDFRONT_COOKIE_NAMES = ["CloudFront-Policy", "CloudFront-Signature", "CloudFront-Key-Pair-Id"];
export const COOKIE_OWNER_NAME = "AmbiFi-Asset-Cookie-Owner";
export const IMAGE_RETRY_PARAM = "ambifiCookieRetry";
const COOKIE_DOMAIN = ".ambifi.com";
const COOKIE_LIFETIME_DAYS = 7;
const CHECK_INTERVAL_MS = 2000;
// An image that failed this soon after the guard put the cookies back is loaded again, once.
const IMAGE_RETRY_WINDOW_MS = 1000 * 10;
// An image that fails this soon after a renewal is missing or not this person's to see, so it doesn't renew again.
const RENEWAL_INTERVAL_MS = 1000 * 60 * 2;
// A new frame (the player) is watched for failed images as soon as its document appears.
const NEW_FRAME_CHECK_DELAYS_MS = [0, 100, 250, 500, 1000, 2000, 4000];
const MAX_FRAME_DEPTH = 2;

/**
 * The cookies a document can read, by name. When a name appears twice, the first is kept: browsers send it first.
 */
export function parseCookies(cookieString = "") {
	const cookies = {};
	_.toString(cookieString).split(";").forEach(part => {
		const separator = part.indexOf("=");
		if (separator <= 0) {
			return;
		}
		const name = part.slice(0, separator).trim();
		if (!name || _.has(cookies, name)) {
			return;
		}
		let value = part.slice(separator + 1).trim();
		try {
			value = decodeURIComponent(value);
		} catch (_err) {
			// Keep the value as it is.
		}
		cookies[name] = value;
	});
	return cookies;
}

function defaultDecodeBase64(text) {
	return window.atob(text);
}

/**
 * The resource and expiry a CloudFront-Policy cookie signs for, or null when it can't be read.
 */
export function decodeCloudFrontPolicy(policy, decodeBase64 = defaultDecodeBase64) {
	if (!_.isString(policy) || policy === "") {
		return null;
	}
	try {
		const json = JSON.parse(decodeBase64(policy.replace(/-/g, "+").replace(/_/g, "=").replace(/~/g, "/")));
		const statement = _.get(json, ["Statement", 0]) || {};
		const epochSeconds = _.get(statement, ["Condition", "DateLessThan", "AWS:EpochTime"]);
		return {
			resource: _.toString(statement.Resource),
			expiresMs: _.isFinite(epochSeconds) ? epochSeconds * 1000 : null
		};
	} catch (_err) {
		return null;
	}
}

/**
 * Whether a policy's resource (CloudFront wildcards: * any run of characters, ? one character) covers the URL.
 */
export function policyCoversUrl(resource, url) {
	if (!_.isString(resource) || resource === "" || !_.isString(url) || url === "") {
		return false;
	}
	const pattern = resource.split("*").map(part => _.escapeRegExp(part).replace(/\\\?/g, ".")).join(".*");
	return new RegExp(`^${pattern}$`).test(url);
}

function endsNoSooner(policy, otherPolicy) {
	return policy.expiresMs !== null && (otherPolicy.expiresMs === null || policy.expiresMs >= otherPolicy.expiresMs);
}

/**
 * Do the cookies in the browser open this site's private files for as long as this tab's own would? They must be signed
 * with this site's key for the same resource and end no sooner than this tab's: another tab of the same site may have
 * written newer ones, which serve as well. End times are compared with each other, not with this computer's clock, which
 * can be off by minutes or hours.
 */
export function cookiesServeSite(cookies, ours, decodeBase64 = defaultDecodeBase64) {
	const keyPairId = cookies["CloudFront-Key-Pair-Id"];
	const policy = cookies["CloudFront-Policy"];
	const signature = cookies["CloudFront-Signature"];
	if (!keyPairId || !policy || !signature || keyPairId !== ours["CloudFront-Key-Pair-Id"]) {
		return false;
	}
	if (policy === ours["CloudFront-Policy"]) {
		return signature === ours["CloudFront-Signature"];
	}
	const browserPolicy = decodeCloudFrontPolicy(policy, decodeBase64);
	const ourPolicy = decodeCloudFrontPolicy(ours["CloudFront-Policy"], decodeBase64);
	return !!ourPolicy && !!browserPolicy && browserPolicy.resource === ourPolicy.resource && endsNoSooner(browserPolicy, ourPolicy);
}

// Browsers keep .ambifi.com cookies only for pages on ambifi.com; elsewhere (localhost) the guard does nothing.
function isAmbifiHost(host) {
	const hostname = _.toLower(_.toString(host).split(":")[0]);
	return hostname === COOKIE_DOMAIN.slice(1) || _.endsWith(hostname, COOKIE_DOMAIN);
}

function appendRetryParam(url, nowMs) {
	const hashIndex = url.indexOf("#");
	const base = hashIndex === -1 ? url : url.slice(0, hashIndex);
	const hash = hashIndex === -1 ? "" : url.slice(hashIndex);
	return `${base}${base.indexOf("?") === -1 ? "?" : "&"}${IMAGE_RETRY_PARAM}=${nowMs.toString(36)}${hash}`;
}

function browserEnvironment() {
	return {
		rootWindow: () => window,
		readCookies: () => document.cookie,
		writeCookie: text => {
			document.cookie = text;
		},
		isVisible: () => document.visibilityState !== "hidden",
		hasFocus: () => !_.isFunction(document.hasFocus) || document.hasFocus(),
		host: () => window.location.host,
		now: () => Date.now(),
		setTimeout: (callback, delayMs) => window.setTimeout(callback, delayMs),
		decodeBase64: defaultDecodeBase64
	};
}

/**
 * @param {object} options
 * @param {function(): object} options.getCookies This tab's own CloudFront cookies, by name (the last /auth answer).
 * @param {function(): Promise} [options.renewCredentials] Renews this tab's credentials and cookies.
 * @param {object} [options.environment] The browser, replaced in tests.
 */
export function createCloudFrontCookieGuard({ getCookies, renewCredentials = null, environment = null }) {
	let browser = environment;
	let lastRepairAtMs = 0;
	let renewal = null;
	let lastRenewalAtMs = null;
	// Images that failed with this site's cookies in place, loaded again once the renewal ends.
	let imagesAwaitingRenewal = [];
	let intervalId = null;
	let observer = null;
	let cookieStore = null;
	let watchedWindows = [];

	const env = () => {
		if (!browser) {
			browser = browserEnvironment();
		}
		return browser;
	};

	const getOurCookies = () => {
		const cookies = (getCookies && getCookies()) || {};
		return CLOUDFRONT_COOKIE_NAMES.every(name => _.isString(cookies[name]) && cookies[name] !== "") ? cookies : null;
	};

	const writeCookie = (name, value) => {
		const expires = new Date(env().now() + 1000 * 60 * 60 * 24 * COOKIE_LIFETIME_DAYS).toUTCString();
		env().writeCookie(`${name}=${encodeURIComponent(value)}; Domain=${COOKIE_DOMAIN}; Path=/; Expires=${expires}`);
	};

	/**
	 * Put this tab's cookies back when the browser holds another site's, none, or older ones of this site.
	 * @param {{claim?: boolean}} [options] claim: the tab was just shown, focused or signed in, so it takes the cookies
	 *   even from another AmbiFi site that shows at the same time.
	 * @returns {boolean} Whether the cookies were written.
	 */
	const sync = ({ claim = false } = {}) => {
		const ours = getOurCookies();
		const host = env().host();
		if (!ours || !env().isVisible() || !isAmbifiHost(host)) {
			return false;
		}
		const browserCookies = parseCookies(env().readCookies());
		if (cookiesServeSite(browserCookies, ours, env().decodeBase64)) {
			if (claim && browserCookies[COOKIE_OWNER_NAME] !== host) {
				writeCookie(COOKIE_OWNER_NAME, host);
			}
			return false;
		}
		const owner = browserCookies[COOKIE_OWNER_NAME];
		if (!claim && owner && owner !== host && !env().hasFocus()) {
			// Another AmbiFi site showing at the same time was used last; it keeps them.
			return false;
		}
		// Written again even right after this tab wrote them: another site writing its cookies one by one at the same
		// moment can leave a mixed set behind (its signature with this site's policy) that opens nothing. The owner goes
		// first, so another AmbiFi tab that reads the cookies halfway through already sees that this site holds them.
		writeCookie(COOKIE_OWNER_NAME, host);
		CLOUDFRONT_COOKIE_NAMES.forEach(name => writeCookie(name, ours[name]));
		lastRepairAtMs = env().now();
		retryImagesAwaitingRenewal();
		return true;
	};

	const retryImage = (element, url) => {
		if (_.toString(element.currentSrc || element.src) === url) {
			element.src = appendRetryParam(url, env().now());
		}
	};

	// Once renewed cookies are in the browser. A hidden tab keeps the images until it shows and writes them.
	const retryImagesAwaitingRenewal = () => {
		if (renewal || !env().isVisible()) {
			return;
		}
		const images = imagesAwaitingRenewal;
		imagesAwaitingRenewal = [];
		images.forEach(({ element, url }) => retryImage(element, url));
	};

	const renewForImage = (element, url) => {
		if (!_.isFunction(renewCredentials)) {
			return;
		}
		if (!renewal && lastRenewalAtMs !== null && env().now() - lastRenewalAtMs < RENEWAL_INTERVAL_MS) {
			return;
		}
		imagesAwaitingRenewal.push({ element, url });
		if (renewal) {
			return;
		}
		renewal = Promise.resolve()
			.then(() => renewCredentials())
			.then(() => true, () => false)
			.then(renewed => {
				renewal = null;
				lastRenewalAtMs = env().now();
				// A failed refresh reports itself and tries again on its own; the images wait for the cookies it writes.
				if (renewed) {
					sync();
					retryImagesAwaitingRenewal();
				}
			});
	};

	const handleResourceError = event => {
		const element = event && event.target;
		if (!element || _.toUpper(element.tagName) !== "IMG") {
			return;
		}
		const url = _.toString(element.currentSrc || element.src);
		if (url === "" || url.indexOf(`${IMAGE_RETRY_PARAM}=`) !== -1) {
			return;
		}
		const ours = getOurCookies();
		const policy = ours && decodeCloudFrontPolicy(ours["CloudFront-Policy"], env().decodeBase64);
		if (!policy || !policyCoversUrl(policy.resource, url)) {
			return;
		}
		if (sync() || env().now() - lastRepairAtMs < IMAGE_RETRY_WINDOW_MS) {
			element.src = appendRetryParam(url, env().now());
			return;
		}
		renewForImage(element, url);
	};

	// Listeners belong to the window object, which a frame replaces when it navigates; the mark goes with it.
	const watchWindow = target => {
		try {
			if (!target || target.__ambifiCloudFrontCookieGuard === handleResourceError) {
				return;
			}
			target.addEventListener("error", handleResourceError, true);
			target.__ambifiCloudFrontCookieGuard = handleResourceError;
			watchedWindows.push(target);
		} catch (_err) {
			// Another origin's frame.
		}
	};

	const watchFrames = (root, depth = 0) => {
		if (!root || depth > MAX_FRAME_DEPTH) {
			return;
		}
		let frames = null;
		try {
			frames = root.frames;
		} catch (_err) {
			return;
		}
		const count = frames ? frames.length : 0;
		for (let index = 0; index < count; index += 1) {
			let frame = null;
			try {
				frame = frames[index];
				// Reading another origin's document throws.
				if (!frame || !frame.document) {
					continue;
				}
			} catch (_err) {
				continue;
			}
			watchWindow(frame);
			watchFrames(frame, depth + 1);
		}
	};

	const check = () => {
		sync();
		watchFrames(env().rootWindow());
	};

	const handleShown = () => {
		if (env().isVisible()) {
			sync({ claim: true });
		}
	};

	const handleCookieChange = event => {
		const changed = _.map(_.concat(_.get(event, "changed", []), _.get(event, "deleted", [])), "name");
		if (_.some(changed, name => _.includes(CLOUDFRONT_COOKIE_NAMES, name))) {
			sync();
		}
	};

	const handleMutations = mutations => {
		const frameAdded = _.some(mutations, mutation => _.some(mutation.addedNodes, node => node && node.nodeType === 1
			&& (_.toUpper(node.tagName) === "IFRAME" || (_.isFunction(node.getElementsByTagName) && node.getElementsByTagName("iframe").length > 0))));
		if (!frameAdded) {
			return;
		}
		sync();
		NEW_FRAME_CHECK_DELAYS_MS.forEach(delayMs => env().setTimeout(() => watchFrames(env().rootWindow()), delayMs));
	};

	const start = () => {
		if (intervalId) {
			return;
		}
		const root = env().rootWindow();
		watchWindow(root);
		root.document.addEventListener("visibilitychange", handleShown);
		root.addEventListener("focus", handleShown);
		root.addEventListener("pageshow", handleShown);
		intervalId = root.setInterval(check, CHECK_INTERVAL_MS);
		try {
			if (root.MutationObserver && root.document.documentElement) {
				observer = new root.MutationObserver(handleMutations);
				observer.observe(root.document.documentElement, { childList: true, subtree: true });
			}
		} catch (_err) {
			observer = null;
		}
		try {
			// Chrome and Edge say at once when another tab writes cookies; elsewhere the two-second check covers it.
			if (root.cookieStore && _.isFunction(root.cookieStore.addEventListener)) {
				root.cookieStore.addEventListener("change", handleCookieChange);
				cookieStore = root.cookieStore;
			}
		} catch (_err) {
			cookieStore = null;
		}
		check();
	};

	const stop = () => {
		if (!intervalId) {
			return;
		}
		const root = env().rootWindow();
		root.clearInterval(intervalId);
		intervalId = null;
		root.document.removeEventListener("visibilitychange", handleShown);
		root.removeEventListener("focus", handleShown);
		root.removeEventListener("pageshow", handleShown);
		if (observer) {
			observer.disconnect();
			observer = null;
		}
		if (cookieStore) {
			cookieStore.removeEventListener("change", handleCookieChange);
			cookieStore = null;
		}
		watchedWindows.forEach(target => {
			try {
				target.removeEventListener("error", handleResourceError, true);
				delete target.__ambifiCloudFrontCookieGuard;
			} catch (_err) {
				// The frame is gone or navigated elsewhere.
			}
		});
		watchedWindows = [];
	};

	return { sync, start, stop, handleResourceError };
}
